import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePostLoginRedirect, resolvePostLoginTarget } from '../../hooks/usePostLoginRedirect';
import { Form, Button, Card, Container, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { getApiErrorMessage } from '../../utils/apiErrorMessages';

/** Default tenant when only one company uses the system; still overridable in the form. */
const DEFAULT_COMPANY_ID = 'discoverseis';

function readDemoQueryParams(search) {
    const params = new URLSearchParams(search);
    const tenant = params.get('tenant')?.trim().toLowerCase() || '';
    const magic = params.get('magic')?.trim() || '';
    const demoUser = params.get('user')?.trim() || '';
    return { tenant, magic, demoUser };
}

/**
 * Offer username/password to the browser credential manager (HTTPS only).
 * Helps mobile (e.g. Chrome/Android) suggest saved passwords on the next visit.
 * Session length is still governed by the JWT in localStorage, not this API.
 */
function offerCredentialsToBrowser(username, password) {
    if (typeof window === 'undefined' || !window.isSecureContext) return;
    const PasswordCredentialCtor = window.PasswordCredential;
    if (!navigator.credentials || !PasswordCredentialCtor) return;
    if (!username || !password) return;
    const cred = new PasswordCredentialCtor({ id: username, password, name: username });
    navigator.credentials.store(cred).catch(() => {});
}

const LoginForm = ({ isModal = false, onClose = null }) => {
    const location = useLocation();
    const initialDemo = readDemoQueryParams(location.search);

    // Load saved values; demo invitation links override tenant/username via query string.
    const [username, setUsername] = useState(() => {
        if (initialDemo.demoUser) return initialDemo.demoUser;
        if (initialDemo.tenant) return '';
        return localStorage.getItem('lastUsername') || '';
    });
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [tenant, setTenant] = useState(
        () => initialDemo.tenant || localStorage.getItem('lastCompanyId') || DEFAULT_COMPANY_ID
    );
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const submitLockRef = React.useRef(false);
    const clearedStaleSessionRef = React.useRef(false);
    const auth = useAuth();
    const navigate = useNavigate();

    usePostLoginRedirect({ enabled: !isModal });

    useEffect(() => {
        const { tenant: t, demoUser, magic } = readDemoQueryParams(location.search);
        if (t) {
            setTenant(t);
            localStorage.setItem('lastCompanyId', t);
        }
        if (demoUser) {
            setUsername(demoUser);
        }
        if (t && !clearedStaleSessionRef.current) {
            clearedStaleSessionRef.current = true;
            // Avoid discoverseis (or other) JWT hitting the API while signing into ciademo demo.
            auth.logout();
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps -- logout once per demo URL

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitLockRef.current) return;
        submitLockRef.current = true;
        setError('');
        setIsLoading(true);
        
        try {
            // Save username and company ID for convenience (not password for security)
            localStorage.setItem('lastUsername', username);
            localStorage.setItem('lastCompanyId', tenant);
            
            // Enviamos el tenant junto con las credenciales
            await auth.login(username, password, tenant);
            offerCredentialsToBrowser(username, password);
            // Single, deterministic post-login redirect for BOTH modal and /login page.
            // (completeLoginWithToken already set authStatus='authenticated' + token, so
            //  PrivateRoute renders /app without bouncing back to /login.)
            const target = resolvePostLoginTarget();
            if (isModal && onClose) onClose();
            navigate(target, { replace: true });
        } catch (err) {
            const status = err?.response?.status;
            const baseMsg = getApiErrorMessage(
                err,
                'Failed to log in. Please check your credentials and company ID.',
            );
            setError(
                status === 401
                    ? `${baseMsg} Use the demo username from your invitation (often ending in .demo), company ID ciademo, and the temporary password from the email.`
                    : baseMsg,
            );
            console.error(err);
        } finally {
            setIsLoading(false);
            submitLockRef.current = false;
        }
    };

    const handleCancel = () => {
        if (isModal && onClose) {
            onClose();
        } else {
            navigate('/');
        }
    };

    // Detectar si estamos en la ruta /login para aplicar estilos diferentes
    const isLoginRoute = location.pathname === '/login';

    // Función para renderizar el formulario (reutilizable)
    const renderLoginForm = () => (
        <Form onSubmit={handleSubmit}>
            <Form.Group id="tenant" className="mb-3">
                <Form.Label style={{ color: 'var(--archon-login-text-label)', fontSize: '0.9rem', fontWeight: '500' }}>Company ID</Form.Label>
                <Form.Control
                    type="text"
                    name="organization"
                    required
                    value={tenant}
                    onChange={(e) => setTenant(e.target.value.toLowerCase().trim())}
                    placeholder="Enter your company ID"
                    style={{ 
                        backgroundColor: 'var(--archon-login-input-bg)', 
                        color: 'var(--archon-login-text)',
                        border: '1px solid var(--archon-login-border)',
                        borderRadius: '6px',
                        padding: '10px 12px'
                    }}
                    autoComplete="organization"
                />
                <Form.Text style={{ fontSize: '0.8rem', color: 'var(--archon-login-text-hint)' }}>
                    Your company identifier (provided by your administrator)
                </Form.Text>
            </Form.Group>

            <Form.Group id="username" className="mb-3">
                <Form.Label style={{ color: 'var(--archon-login-text-label)', fontSize: '0.9rem', fontWeight: '500' }}>Username</Form.Label>
                <Form.Control
                    type="text"
                    name="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    style={{ 
                        backgroundColor: 'var(--archon-login-input-bg)', 
                        color: 'var(--archon-login-text)',
                        border: '1px solid var(--archon-login-border)',
                        borderRadius: '6px',
                        padding: '10px 12px'
                    }}
                    autoComplete="username"
                />
            </Form.Group>
            <Form.Group id="password" className="mb-4">
                <Form.Label style={{ color: 'var(--archon-login-text-label)', fontSize: '0.9rem', fontWeight: '500' }}>Password</Form.Label>
                <InputGroup>
                    <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        style={{ 
                            backgroundColor: 'var(--archon-login-input-bg)', 
                            color: 'var(--archon-login-text)',
                            border: '1px solid var(--archon-login-border)',
                            borderRadius: '6px 0 0 6px',
                            padding: '10px 12px'
                        }}
                        autoComplete="current-password"
                    />
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                            backgroundColor: 'var(--archon-login-input-bg)',
                            border: '1px solid var(--archon-login-border)',
                            borderLeft: 'none',
                            borderRadius: '0 6px 6px 0',
                            color: 'var(--archon-login-text-label)'
                        }}
                    >
                        {showPassword ? <BsEyeSlash size={18} /> : <BsEye size={18} />}
                    </Button>
                </InputGroup>
            </Form.Group>
            <Button 
                className="w-100 mb-2" 
                type="submit" 
                size="lg"
                disabled={isLoading}
                style={{
                    backgroundColor: 'var(--archon-login-primary)',
                    border: 'none',
                    color: 'var(--archon-login-heading)',
                    fontWeight: '600',
                    borderRadius: '6px',
                    padding: '10px',
                    transition: 'all 0.2s ease',
                    opacity: isLoading ? 0.7 : 1
                }}
                onMouseOver={(e) => !isLoading && (e.target.style.backgroundColor = 'var(--archon-login-primary-hover)')}
                onMouseOut={(e) => e.target.style.backgroundColor = 'var(--archon-login-primary)'}
            >
                {isLoading ? (
                    <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Logging in...
                    </>
                ) : 'Log In'}
            </Button>
            {(isModal || isLoginRoute) && (
                <Button 
                    className="w-100" 
                    variant="outline-secondary"
                    size="lg"
                    onClick={handleCancel}
                    style={{
                        borderColor: 'var(--archon-login-border-strong)',
                        color: 'var(--archon-login-text-label)',
                        fontWeight: '500',
                        borderRadius: '6px',
                        padding: '10px',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                        e.target.style.borderColor = 'var(--archon-login-border-hover)';
                        e.target.style.color = 'var(--archon-login-text)';
                    }}
                    onMouseOut={(e) => {
                        e.target.style.borderColor = 'var(--archon-login-border-strong)';
                        e.target.style.color = 'var(--archon-login-text-label)';
                    }}
                >
                    Cancel
                </Button>
            )}
        </Form>
    );

    // Si es modal, crear ventana flotante sobre el landing page
    if (isModal) {
        return (
            <div style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
                padding: '40px',
                maxWidth: '480px',
                width: 'calc(100% - 40px)',
                backgroundColor: 'var(--archon-login-overlay-bg)',
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                boxShadow: 'var(--archon-login-shadow-modal)'
            }}>
                <Card className="border-0 login-card-dark" style={{ 
                    boxShadow: 'none',
                    borderRadius: '12px',
                    backgroundColor: 'transparent'
                }}>
                    <Card.Body className="p-4">
                        <h2 className="text-center mb-4" style={{ color: 'var(--archon-login-heading)', fontWeight: '600', letterSpacing: '2px' }}>ArchonPM</h2>
                        <p className="text-center mb-4" style={{ color: 'var(--archon-login-heading)', fontSize: '0.95rem' }}>Sign in to your account</p>
                        {error && <Alert variant="danger">{error}</Alert>}
                        {renderLoginForm()}
                    </Card.Body>
                </Card>
            </div>
        );
    }

    // Modo página normal
    return (
        <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
            <div className="w-100" style={{ maxWidth: "400px" }}>
                <Card className="border-0 login-card-dark" style={{ 
                    boxShadow: 'var(--archon-login-shadow-card)',
                    borderRadius: '12px'
                }}>
                    <Card.Body className="p-4">
                        <h2 className="text-center mb-4" style={{ color: 'var(--archon-login-heading)', fontWeight: '600', letterSpacing: '2px' }}>ArchonPM</h2>
                        <p className="text-center mb-4" style={{ color: 'var(--archon-login-heading)', fontSize: '0.95rem' }}>Sign in to your account</p>
                        {error && <Alert variant="danger">{error}</Alert>}
                        {renderLoginForm()}
                    </Card.Body>
                </Card>
            </div>
        </Container>
    );
};

export default LoginForm;